import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  TrendingUp, AlertTriangle, DollarSign, Shield, 
  Cloud, Zap, Bot, Users, Building2, Check, ArrowRight, 
  Play, ChevronLeft, ChevronRight, Sparkles, Clock, 
  GitBranch, Bell, Mic, Brain, AlertOctagon,
  Monitor, FileText, Trophy, Search, Tags, ArrowRightLeft,
  BarChart3, TrendingDown, Activity, Cpu, Presentation, Handshake, ShieldCheck,
  Leaf, Calculator, Calendar, Menu, X, FileCheck,
  ChevronDown, Code2, Scale, Workflow, Gauge, Lock, Scissors,
  Crosshair, BookOpen, Terminal
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import BookDemoModal from '../components/BookDemoModal';
import LivePilotSavingsBadge from '../components/LivePilotSavingsBadge';
// v26: ProblemSolutionCarousel import removed — the "13 Wins" carousel was deleted
// from Landing because it overlapped with the Engines grid + Free Tools strip.
// The component still exists at components/ProblemSolutionCarousel.js for future reuse.
import LiveTradingChart from '../components/LiveTradingChart';
import RotatingCatchTicker from '../components/RotatingCatchTicker';
import ProductWalkthrough from '../components/ProductWalkthrough';
import NewsletterSignup from '../components/NewsletterSignup';
import HeroBackground from '../components/HeroBackground';
import FooterBackground from '../components/FooterBackground';
import FreeToolsCarousel from '../components/FreeToolsCarousel';
import DiveDeeperMarquee from '../components/DiveDeeperMarquee';
import ScrollReveal from '../components/ScrollReveal';
import CountUp from '../components/CountUp';
import ScrollProgress from '../components/ScrollProgress';
import WordReveal from '../components/WordReveal';
import MagneticButton from '../components/MagneticButton';
import EnginesMarquee from '../components/EnginesMarquee';
import VisitorCounter from '../components/VisitorCounter'; // eslint-disable-line no-unused-vars
import CloudIntegrationsCarousel from '../components/CloudIntegrationsCarousel';
import HeroDashboardMockup from '../components/HeroDashboardMockup';
import HeroSkeletonReveal from '../components/HeroSkeletonReveal'; // v34.68 — Nova anti-vanity boot animation
import LiveTerminalHero from '../components/LiveTerminalHero';
// v34.29 (Feb 2026): ScorecardTeaser import removed — its message now
// lives inside EnginesMarquee so visitors don't see "26 ..." twice on
// the landing. The component still exists in /components/ for reuse.
import DeveloperQuickstart from '../components/DeveloperQuickstart';
import EmeraldCursor from '../components/EmeraldCursor';
import useParallax from '../hooks/useParallax';
import TopReadsWidget from '../components/TopReadsWidget';
import WallOfLove from '../components/WallOfLove'; // v34.66 — pre-launch testimonial slot
import TrustStrip from '../components/landing/TrustStrip';
import DiveDeeperSection from '../components/landing/DiveDeeperSection';
import TotalCostOfAiTeaser from '../components/landing/TotalCostOfAiTeaser';
import WhoNotFor from '../components/WhoNotFor'; // v34.67 — anti-sales exclusion list (Nova review)
import PilotWall from '../components/PilotWall'; // v34.67 — founding-10 engineers wall
import ConsolidationMath from '../components/ConsolidationMath'; // v34.68 — Vantage+Datadog+CloudZero vs CARTIE math
import CausalAttribution from '../components/CausalAttribution'; // v34.68 — spike → line of code in 11s (Nova R4 #2)
import CartieCliMockup from '../components/CartieCliMockup'; // v34.69 — terminal mockup companion to Causal Attribution
import SpotInferenceArbitrageur from '../components/SpotInferenceArbitrageur'; // v34.69 — Real-Time Token Arbitrage (Nova R4 #1)
import OneClickRefund from '../components/OneClickRefund'; // v34.70 — Nova R4 #4 SLA refund auto-drafter
import RoiCalculator from '../components/RoiCalculator'; // v34.70 — interactive savings + email capture
import ReliabilityStrip from '../components/ReliabilityStrip'; // v34.70 — "boringly reliable" trust counters
import AiCfoBriefing from '../components/AiCfoBriefing'; // v34.71 — CFO-side dramatic moment (balances engineer-heavy sections)
import CliInstallStrip from '../components/landing/CliInstallStrip'; // v34.99 (Feb 2026) — extracted CLI strip
import AntiLockInStrip from '../components/landing/AntiLockInStrip'; // v34.99 (Feb 2026) — extracted anti-lock-in strip
import AntiLockInGuarantee from '../components/AntiLockInGuarantee'; // v34.68 — one-line-to-leave promise
import EncryptionManifest from '../components/EncryptionManifest'; // v34.68 — AES-256/TLS 1.3/Vanta hard-infra trust
import HNLaunchCountdown from '../components/HNLaunchCountdown';
// import LaunchCounter from '../components/LaunchCounter'; // v34: removed from hero
import SOC2TrustBadge from '../components/SOC2TrustBadge';
// Iter84f — LiveBuildPill, BuiltInPublicChip, BuildMomentumTeaser imports
// kept but unused; Build-in-Public surface hidden. Lint might warn but the
// components are still used by other pages / admin flows.
// import LiveBuildPill from '../components/LiveBuildPill';
// import BuiltInPublicChip from '../components/BuiltInPublicChip';
// import BuildMomentumTeaser from '../components/BuildMomentumTeaser';
import SEOHead from '../components/SEOHead';
import WhatCartieCanDoNow from '../components/WhatCartieCanDoNow'; // v34.91 — 3-card "do it right now" outcome row
import TourVideo from '../components/landing/TourVideo'; // v35.44 — narrated 7-min full app tour
import NavbarVisitorPill from '../components/NavbarVisitorPill'; // v34.92 — live visitor count next to Sign In
import FAQSection from '../components/FAQSection';
import { faqsByPage } from '../data/faqs';
import { useAuth } from '../context/AuthContext';
import { trackLandingPageView, trackDemoCTAClick } from '../hooks/useAnalytics';
import { ENGINE_COUNT } from '../constants/product';

const Landing = () => {
  const [demoLoading, setDemoLoading] = useState(false);
  const [wasteCounter, setWasteCounter] = useState(0);
  const [currentKillerSlide, setCurrentKillerSlide] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [bookDemoOpen, setBookDemoOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // v34.54 (Feb 2026) — interactive AI spend slider for the "Total Cost of AI"
  // section. Replaces the static $1M claim with a personal-spend slider so each
  // visitor sees THEIR own number across the 40/30/30 split.
  const [aiSpendUSD, setAiSpendUSD] = useState(250000);
  const heroRef = useRef(null);
  const { demoLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { trackLandingPageView(); }, []);

  // Smooth scroll + pulse-highlight handler for in-page anchor nav links.
  // Replaces the default abrupt #hash jump with a premium scroll + brief
  // emerald glow on the target section. Adds the navigated section to
  // the URL hash so the back button still works.
  const smoothScrollTo = (e, id) => {
    if (e?.preventDefault) e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96; // sticky nav clearance
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    // History push for back-button parity (no jump because we already scrolled).
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${id}`);
    }
    // Pulse-highlight the target so the user knows where they landed.
    el.classList.add('section-target-pulse');
    window.setTimeout(() => el.classList.remove('section-target-pulse'), 1800);
  };

  // v34.1 (Mar 2026): scroll-tracked parallax on the hero's decorative glow.
  // Subtle drift — viewport-center based.
  const heroParallax = useParallax(0.18);

  // Live waste counter - counts up creating urgency
  useEffect(() => {
    const start = Date.now();
    const rate = 4127; // $4,127 per second wasted globally
    const timer = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      setWasteCounter(Math.floor(elapsed * rate));
    }, 50);
    return () => clearInterval(timer);
  }, []);

  // Mouse tracking for particle effect
  useEffect(() => {
    const handleMouse = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
      }
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  // Auto-advance killer features
  useEffect(() => {
    const timer = setInterval(() => setCurrentKillerSlide((p) => (p + 1) % 2), 5000);
    return () => clearInterval(timer);
  }, []);

  // Live forecasting accuracy — backtest of our own engine, shown as a
  // public trust signal. NEVER hard-code a marketing accuracy number;
  // we publish the math.
  const [forecastAccuracy, setForecastAccuracy] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forecasting/accuracy`)
      .then((r) => r.json())
      .then((d) => setForecastAccuracy(d))
      .catch(() => {});
  }, []);

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      await demoLogin();
      trackDemoCTAClick();
      navigate('/dashboard');
    } catch (err) {
      // Log full error so we can debug from console / Sentry-style tooling.
      // The toast is intentionally specific so users know whether to retry
      // or check their network.
      console.error('[demoLogin] failed:', err);
      const isNetwork = !err?.response;
      if (isNetwork) {
        toast.error('Network hiccup — check your connection and click Try Demo again.');
      } else if (err.response.status === 503 || err.response.status === 504) {
        toast.error('Demo is warming up. Try again in a few seconds.');
      } else {
        toast.error(
          `Demo login failed (HTTP ${err.response.status}). Click Try Demo again — usually resolves on retry.`
        );
      }
    } finally {
      setDemoLoading(false);
    }
  };

  const killerFeatures = [
    { icon: FileText, title: 'Invoice Autopsy', desc: 'Upload your cloud bill. AI explains every spike in 60 seconds.', path: '/invoice-autopsy', color: 'violet', tag: 'NEW' },
    { icon: Trophy, title: 'Cost Scorecard', desc: 'Weekly team accountability rankings with gamified leaderboards.', path: '/bill-detective', color: 'amber', tag: 'NEW' },
    { icon: Search, title: 'SaaS Detective', desc: 'Find hidden subscriptions & duplicate tools eating your budget.', path: '/saas-detective', color: 'blue', tag: 'NEW' },
    { icon: Tags, title: 'Smart Tag Fixer', desc: 'AI auto-tags 50% of your untagged cloud resources. One click.', path: '/smart-tags', color: 'teal', tag: 'NEW' },
    { icon: ArrowRightLeft, title: 'Egress Shield', desc: 'Map data flows. Predict egress costs before they surprise you.', path: '/egress-shield', color: 'rose', tag: 'NEW' },
    { icon: Presentation, title: 'Board Deck Generator', desc: 'CFO-ready quarterly presentations in 30 seconds. Not days.', path: '/board-deck', color: 'indigo', tag: 'NEW' },
    { icon: Handshake, title: 'Vendor Deal Intel', desc: 'Know what you should pay. AI-powered contract negotiation.', path: '/vendor-deals', color: 'orange', tag: 'NEW' },
    { icon: ShieldCheck, title: 'Compliance Mapper', desc: 'Auto-generate SOC 2 & ISO 27001 audit evidence from cloud data.', path: '/compliance-mapper', color: 'sky', tag: 'NEW' },
    { icon: Leaf, title: 'Carbon + ESG', desc: 'Track CO2 emissions. ESG-ready reports. Meet sustainability goals.', path: '/carbon', color: 'emerald', tag: 'HOT' },
    { icon: Calculator, title: 'Unit Economics', desc: 'Cost per customer, per feature, per API call. Price right.', path: '/unit-economics', color: 'blue', tag: 'NEW' },
    { icon: Cpu, title: 'AI/GPU Intelligence', desc: 'Track every token-to-dollar. GPU utilization in real-time.', path: '/ai-gpu', color: 'purple', tag: 'HOT' },
    { icon: Zap, title: 'Zombie Killer', desc: 'Find and eliminate idle resources wasting thousands monthly.', path: '/zombie-killer', color: 'emerald', tag: 'HOT' },
    { icon: AlertOctagon, title: 'Commitment Intel', desc: 'Cross-cloud RI/SP optimizer with renewal alerts.', path: '/commitment-intel', color: 'red', tag: 'HOT' },
  ];

  const stats = [
    { value: 'LIVE', label: 'Median Pilot Savings', live: true },
    { value: '90d', label: 'Refund Window' },
    { value: String(ENGINE_COUNT), label: 'Smart Engines' },
    { value: '132', label: 'Math Tests Passing' },
  ];

  const colorMap = {
    violet: 'from-violet-500 to-purple-600', amber: 'from-amber-500 to-orange-500',
    blue: 'from-blue-500 to-indigo-600', teal: 'from-teal-500 to-emerald-600',
    rose: 'from-rose-500 to-pink-600', purple: 'from-purple-500 to-violet-600',
    emerald: 'from-emerald-500 to-green-600', red: 'from-red-500 to-rose-600',
    indigo: 'from-indigo-500 to-blue-600', orange: 'from-orange-500 to-red-500',
    sky: 'from-sky-500 to-blue-600',
  };

  return (
    <div className="dark min-h-screen bg-[#050505] text-slate-100 overflow-x-hidden font-sans">
      <EmeraldCursor />
      <ScrollProgress />
      <HNLaunchCountdown />
      <SEOHead
        title="CARTIEAI | Pay 60% less for AI tokens — with any model"
        description="Drop CARTIE in front of OpenAI, Anthropic, Gemini, Groq, Mistral. Same prompt, same answer, 60% fewer tokens. Plus a full Financial OS for your cloud bill — Bill Detective, Idle Killer, and Agent Economics. 3× ROI in 90 days or full refund."
        canonical="https://cartieai.com/"
        keywords="AI token cost, LLM cost reduction, OpenAI proxy, Anthropic proxy, Gemini cost, Groq, prompt compaction, semantic cache, FinOps, cloud cost optimization, Bill Detective"
        schema={[
          // ── Organization (top-level brand entity) ──
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CARTIEAI",
            "url": "https://cartieai.com",
            "logo": "https://cartieai.com/icons/cartie-logo.png",
            "description": "CARTIEAI — pay 60% less for AI tokens with a drop-in Auto-Compiler Proxy for OpenAI, Anthropic, Gemini, Groq, and Mistral. Plus a Financial OS for your cloud bill.",
            "sameAs": [
              "https://twitter.com/cartieai",
              "https://www.linkedin.com/company/cartieai",
              "https://github.com/cartieai",
            ],
          },
          // ── SoftwareApplication (the product) ──
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CARTIEAI",
            "applicationCategory": "BusinessApplication",
            "applicationSubCategory": "LLM Cost Optimization · FinOps",
            "operatingSystem": "Web",
            "description": "Drop-in Auto-Compiler Proxy for OpenAI, Anthropic, Gemini, Groq, and Mistral. Pay 20–50% less per call (up to 70% on repetitive workloads) with zero code changes. Plus a full Financial OS covering AWS, Azure, GCP, Snowflake, and Databricks.",
            "url": "https://cartieai.com/",
            "offers": [
              {
                "@type": "Offer",
                "name": "Free tier",
                "price": "0",
                "priceCurrency": "USD",
                "description": "Public /try demo, free tools, and 14-day Pro trial — no credit card required",
              },
              {
                "@type": "Offer",
                "name": "Pro",
                "price": "199",
                "priceCurrency": "USD",
                "priceSpecification": {
                  "@type": "UnitPriceSpecification",
                  "price": "199",
                  "priceCurrency": "USD",
                  "unitCode": "MON",
                  "billingDuration": "P1M",
                },
              },
            ],
            "publisher": {
              "@type": "Organization",
              "name": "CARTIEAI",
              "url": "https://cartieai.com",
            },
            "featureList": [
              "Auto-Compiler Proxy — 20–50% AI token savings on any model (OpenAI, Anthropic, Gemini, Groq, Mistral)",
              "Prompt Compactor — deterministic, semantics-preserving prompt compression",
              "Streaming-aware semantic cache — SSE-safe replay of identical LLM calls",
              "Real-Time Token Arbitrage — cheapest-quality-adjusted model routing across 12 providers",
              "Bill Detective — maps cloud spikes to the exact PR, commit, and engineer",
              "Cloud FinOps for AWS, Azure, GCP, Snowflake, and Databricks",
              `${ENGINE_COUNT} engines shipping, contractually-binding 3× ROI guarantee in 90 days`,
              "Stateless /try public demo — no prompt storage, no IP logging",
            ],
          },
          // ── FAQPage (rich-snippet trigger in Google) ──
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How does CARTIE cut AI token costs by 60%?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The CARTIE Auto-Compiler Proxy sits between your app and any LLM (OpenAI, Anthropic, Gemini, Groq, Mistral). It runs three techniques on every call — deterministic prompt compaction, streaming-aware semantic cache, and optional cheap-model cascade — to deliver the same output using 20–50% fewer tokens on typical prompts and up to 70% on repetitive workloads. No prompt rewrites, no model downgrades, no code changes: just change one line — your base URL.",
                },
              },
              {
                "@type": "Question",
                "name": "Which LLM providers does CARTIE support?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "CARTIE is a drop-in proxy for OpenAI, Anthropic (Claude), Google Gemini, Groq, and Mistral. It exposes the OpenAI-compatible /v1/chat/completions surface so any SDK that speaks OpenAI can point at CARTIE and get instant token savings on any of these providers. Additional model routers watch 12 providers and 33 models total to pick the cheapest-quality-adjusted option per task class.",
                },
              },
              {
                "@type": "Question",
                "name": "Do you store my prompts?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. The public /try demo endpoint is fully stateless — we never store your prompt text, we never store the compacted version, and we never log your IP. This is enforced by unit tests in our CI pipeline. In the authenticated proxy, logs are opt-in and can be redacted or disabled at any time. SOC 2 Type I audit is in progress.",
                },
              },
              {
                "@type": "Question",
                "name": "How is CARTIE different from Helicone or LangSmith?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Helicone and LangSmith are observability layers — they show you the bill, they don't reduce it. CARTIE is a compaction layer: it actively cuts your input token count on every call before it reaches the LLM. You still get full observability (logs, cost per model, p50/p95 latency, error rate) as a bonus, but the primary value is the 20–50% cost cut on the invoice itself.",
                },
              },
              {
                "@type": "Question",
                "name": "Is there a free tier or free trial?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `Yes. Try the Auto-Compiler Proxy live at cartieai.com/try — paste any prompt, pick any model, see real before/after token counts and dollar savings. No signup, no credit card, fully stateless. For the full ${ENGINE_COUNT}-engine platform (proxy, cloud FinOps, Bill Detective, semantic cache), there is a 14-day Pro trial with no credit card required.`,
                },
              },
              {
                "@type": "Question",
                "name": "How is CARTIE priced?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Pro is $199/month or $1,990/year (save 17%). Enterprise is custom. Every subscription is backed by a contractually-binding pledge: 3× return on your subscription in the first 90 days, or a full refund. In writing. No fine print.",
                },
              },
              {
                "@type": "Question",
                "name": "How fast is the setup?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Under 5 minutes. Change one line — swap your OpenAI/Anthropic base URL for the CARTIE proxy URL — and the compactor is live. For the cloud FinOps layer, connect AWS/Azure/GCP via read-only IAM roles and the first anomaly and savings report runs immediately.",
                },
              },
              {
                "@type": "Question",
                "name": "How many engines does CARTIE actually run?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `${ENGINE_COUNT} engines are live today. The Auto-Compiler Proxy (aka Prompt Compactor) is the flagship AI-cost wedge. The other ${ENGINE_COUNT - 1} cover cloud spend attribution (Bill Detective), idle-resource killers, RI/savings-plan coverage, agent economics, per-feature P&L, RAG embedding cache, output-side response diet, conversation memory compaction, spot-inference arbitrage, and more. See cartieai.com/patent-portfolio for the full list.`,
                },
              },
            ],
          },
        ]}
      />
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/60 backdrop-blur-2xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold"><span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup></span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              {/* v34.72 (Jun 9 2026): trimmed navbar — 4 colored links instead of 6.
                  Reality Check + VS Code moved into Resources dropdown.        */}
              <Link
                to="/tools/ai-cost-calculator"
                className="group relative text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-all inline-flex items-center gap-1.5"
                data-testid="nav-ai-cost-calc"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
                Calculator
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-emerald-400 bg-gradient-to-r from-emerald-400/0 via-emerald-400 to-emerald-400/0 scale-x-0 group-hover:scale-x-100 transition-transform" />
              </Link>
              <Link
                to="/patent-portfolio"
                className="group relative text-sm font-semibold text-fuchsia-300 hover:text-fuchsia-200 transition-all inline-flex items-center gap-1.5"
                data-testid="nav-patent-portfolio"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 shadow-[0_0_8px_rgba(232,121,249,0.7)]" />
                Engines
                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-fuchsia-500/15 text-fuchsia-300 font-bold leading-none ring-1 ring-fuchsia-400/30">26</span>
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-fuchsia-400 bg-gradient-to-r from-fuchsia-400/0 via-fuchsia-400 to-fuchsia-400/0 scale-x-0 group-hover:scale-x-100 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                className="group relative text-sm font-semibold text-sky-300 hover:text-sky-200 transition-all inline-flex items-center gap-1.5"
                data-testid="nav-pricing"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.7)]" />
                Pricing
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-sky-400 bg-gradient-to-r from-sky-400/0 via-sky-400 to-sky-400/0 scale-x-0 group-hover:scale-x-100 transition-transform" />
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="group relative text-sm font-semibold text-pink-300 hover:text-pink-200 transition-all inline-flex items-center gap-1.5 outline-none focus:outline-none"
                  data-testid="nav-resources-trigger"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400 shadow-[0_0_8px_rgba(244,114,182,0.7)]" />
                  Resources
                  <ChevronDown className="w-3.5 h-3.5 transition-transform group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border border-white/10 text-slate-200 min-w-[220px] mt-1">
                  <DropdownMenuItem asChild>
                    <Link to="/tools/finops-maturity" className="cursor-pointer w-full" data-testid="nav-maturity-quiz">Reality Check</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/vscode-extension" className="cursor-pointer w-full flex items-center justify-between" data-testid="nav-vscode-ext">
                      <span>VS Code Extension</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-300 font-bold uppercase">BETA</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/blog" className="cursor-pointer w-full">Blog</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/guides" className="cursor-pointer w-full">Guides</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/developers" className="cursor-pointer w-full">Developers</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/sdk" className="cursor-pointer w-full flex items-center justify-between" data-testid="nav-sdk-link">
                      <span>SDK · auto-inject headers</span>
                      <span className="text-[9px] text-emerald-300 ml-2">NEW</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/faq" className="cursor-pointer w-full">FAQ</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/about" className="cursor-pointer w-full">Our Story</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/integrations" className="cursor-pointer w-full">Integrations Hub</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/soc2" className="cursor-pointer w-full flex items-center justify-between">
                      <span>SOC 2 Type I</span>
                      <span className="text-[9px] text-amber-300 ml-2">in progress</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center gap-2">
              {/* LiveBuildPill removed (Iter84f) — Build-in-Public hidden. */}
              {/* v34.92 — Live visitor pill. Sits before Sign In so every
                  arriving visitor sees the global count tick up. Backed by
                  /api/visits — privacy-safe, deduped per-day per-visitor. */}
              <NavbarVisitorPill />
              <Link to="/login" className="hidden sm:inline-flex"><Button variant="ghost" className="text-slate-300 hover:text-white">Sign In</Button></Link>
              <Button onClick={handleDemoLogin} disabled={demoLoading} className="hidden sm:inline-flex bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="nav-demo-btn">
                <Play className="w-4 h-4 mr-1.5 fill-current" />Live Demo
              </Button>
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                data-testid="landing-mobile-toggle"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="landing-mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-[88%] max-w-sm bg-slate-950 border-l border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between h-16 px-4 border-b border-white/5">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <img src="/icons/cartie-emblem.png" alt="" className="w-8 h-8 object-contain" />
                <span className="font-bold text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </Link>
              <button
                type="button"
                className="w-10 h-10 inline-flex items-center justify-center rounded-lg text-slate-300 hover:text-white hover:bg-white/5"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <ul className="space-y-1">
                <li><a href="#features" onClick={(e) => { setMobileMenuOpen(false); smoothScrollTo(e, 'features'); }} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">Features</a></li>
                <li><a href="#killer" onClick={(e) => { setMobileMenuOpen(false); smoothScrollTo(e, 'killer'); }} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">What's New</a></li>
                <li><Link to="/about" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">Our Story</Link></li>
                <li><Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">Pricing</Link></li>
                <li><Link to="/about" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">About</Link></li>
                <li><Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">Blog</Link></li>
                <li><Link to="/guides" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">Guides</Link></li>
                <li><Link to="/developers" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white" data-testid="landing-mobile-developers">Developers</Link></li>
                <li><Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white" data-testid="landing-mobile-faq">FAQ</Link></li>
                <li><Link to="/snowflake-health-score" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20">Free Snowflake Audit</Link></li>
                <li><Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="flex px-3 py-3 rounded-lg text-base font-medium text-slate-300 hover:bg-white/5 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div className="px-4 py-4 border-t border-white/5 space-y-2">
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block">
                <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-white/5">Sign In</Button>
              </Link>
              <Button onClick={() => { setMobileMenuOpen(false); handleDemoLogin(); }} disabled={demoLoading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                <Play className="w-4 h-4 mr-1.5 fill-current" />Live Demo
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== HERO SECTION =====
          v34.20 (Feb 2026): removed `min-h-[90vh] flex items-center` per
          user feedback ("remove the gap"). The hero was vertically
          centering inside a 90vh container which left a giant black band
          below the live signal feed. Now hero just flows naturally and
          the trust bar sits directly underneath. */}
      <section ref={heroRef} className="aurora-hero relative pt-28 pb-12 px-4 overflow-hidden">
        {/* Cinematic animated background — flowing data nodes + mesh gradient */}
        <HeroBackground />

        {/* v34 (Mar 2026): "Skip to the 30-second walkthrough" floating pill
            removed. User feedback: too prominent, blocking the dashboard mockup,
            and visitors naturally scroll to find the walkthrough anyway. */}

        {/* Mouse-tracked accent glows (kept — feels alive)
            v34.1: also parallax-drifts on scroll for layered depth.
            v34.18 (Feb 2026): switched from pure emerald → emerald + sky mix
            per user feedback ("too greenish"). Sky-500 at low opacity acts
            as a navy ambient so the hero reads as cool dark, not green wash. */}
        <div
          ref={heroParallax.ref}
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{ transform: `translate3d(0, ${heroParallax.offset}px, 0)` }}
        >
          <div className="absolute w-[600px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-[120px] transition-all duration-[2000ms] ease-out"
            style={{ left: `${mousePos.x * 60}%`, top: `${mousePos.y * 40}%` }} />
          <div className="absolute w-[480px] h-[480px] rounded-full bg-sky-500/[0.05] blur-[120px] transition-all duration-[3000ms] ease-out"
            style={{ right: `${(1 - mousePos.x) * 40}%`, top: `${mousePos.y * 60}%` }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left - Hero Content */}
            <div className="text-center lg:text-left">
              {/* v34.18 (Feb 2026): Token Intelligence Suite NEW pill — no
                  "Beta — Free Access" duplicate pill per user feedback. */}
              {/* v34.67 (Jun 3 2026) — hero pill rewritten per Nova's brutal review:
                  the link goes to /honest-scorecard so the copy must describe the
                  scorecard, not the engine count. Admitting weaknesses upfront is
                  a stronger trust signal than another "26 engines" badge. */}
              <Link
                to="/honest-scorecard"
                className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-900 px-3 py-1 mb-5 transition-all hover:border-emerald-400/40 animate-fade-in-up"
                style={{ animationDelay: '0.05s' }}
                data-testid="hero-honest-scorecard-strip"
              >
                <Badge className="bg-emerald-500 text-slate-950 text-[10px] font-mono font-bold tracking-wider px-1.5 py-0">HONEST</Badge>
                <span className="text-emerald-200 text-xs font-medium">Where Vantage beats us — and why teams switch anyway</span>
                <span className="text-emerald-400 text-xs group-hover:translate-x-0.5 transition-transform">→</span>
              </Link>

              {/* v34.6 (Mar 2026): Burning Cash pill removed from hero — it competed
                  with the Token Intel + Honest Scorecard strips for the same eyeball
                  space. The live burn ticker still lives on the dashboard at /reasoning-efficiency. */}

              {/* v34.17 (Feb 2026): Honest Scorecard hero pill removed per user
                  feedback ("feels like its dominating"). The compact
                  ScorecardTeaser further down on the page is now the single
                  source for that message — keeps the hero cleaner. */}

              {/* v35.30 (Feb 26 2026) — Hero rewritten after real-customer + CFO +
                  investor feedback ("you sound like 3 products, what makes you different").
                  Single wedge: AI token reduction is the HERO. Cloud FinOps becomes
                  the moat reveal below the fold. Previous hero (v34.90 "FinOps tool
                  engineers don't hate") preserved in git for revert if needed. */}
              <h1 className="font-display text-5xl sm:text-6xl lg:text-[4rem] font-black mb-6 leading-[0.95] tracking-[-0.04em] animate-fade-in-up" style={{ animationDelay: '0.1s' }} data-testid="hero-h1">
                Pay{' '}
                <span className="text-emerald-400 drop-shadow-[0_0_28px_rgba(16,185,129,0.45)]">60% less</span>
                {' '}for AI tokens.
                <span className="block text-slate-200 text-3xl sm:text-4xl lg:text-5xl mt-3 font-bold">
                  Without changing a line of code.
                </span>
              </h1>

              <p className="text-lg text-slate-300 mb-4 max-w-xl animate-fade-in-up leading-relaxed" style={{ animationDelay: '0.2s' }} data-testid="hero-copy-line1">
                Drop CARTIE in front of{' '}
                <span className="text-white font-semibold">OpenAI, Anthropic, Gemini, Groq, Mistral</span> — any model.
                <span className="text-white font-semibold"> Same prompt. Same answer. Up to 60% fewer tokens.</span>
              </p>

              <p className="text-lg text-slate-300 mb-6 max-w-xl animate-fade-in-up leading-relaxed" style={{ animationDelay: '0.25s' }} data-testid="hero-copy-line2">
                Prompt compaction, semantic cache, and model cascade — running automatically on every call.
                <span className="mx-2 inline-block align-middle h-px w-6 bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" aria-hidden="true" />
                <span className="text-emerald-300 font-semibold">Pay less, ship more.</span>
              </p>

              <p className="text-base text-emerald-300/90 mb-6 max-w-xl animate-fade-in-up leading-relaxed" style={{ animationDelay: '0.28s' }} data-testid="hero-platform-line">
                <span className="text-emerald-200 font-semibold">+ Financial OS for your cloud bill</span> · Bill Detective names the engineer, the commit, and the PR behind every cloud cost spike — so cost finally becomes accountable.
              </p>

              <p className="text-base text-slate-400 mb-6 max-w-xl animate-fade-in-up leading-relaxed" style={{ animationDelay: '0.3s' }} data-testid="hero-copy-line3">
                Plugs into{' '}
                <span className="font-mono text-[0.92em] text-emerald-200 bg-emerald-500/5 ring-1 ring-emerald-400/20 px-1.5 py-0.5 rounded">GitHub</span>
                ,{' '}
                <span className="font-mono text-[0.92em] text-emerald-200 bg-emerald-500/5 ring-1 ring-emerald-400/20 px-1.5 py-0.5 rounded">Slack</span>
                , and the tools your team already uses. <span className="text-slate-300">No new dashboards to learn.</span>
              </p>

              {/* PLEDGE — moved to hero (Feb 24 2026, v32).
                  CFOs are tired of "potential" savings. Make the guarantee the headline. */}
              <Link to="/pricing#pledge" className="block mb-8 animate-fade-in-up group" style={{ animationDelay: '0.3s' }} data-testid="hero-pledge-card">
                <div className="relative overflow-hidden inline-flex items-start gap-4 bg-slate-900/60 border border-white/10 rounded-xl px-5 py-4 transition-all hover:border-emerald-400/40 hover:-translate-y-0.5">
                  {/* Soft emerald glow */}
                  <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-400/15 blur-2xl pointer-events-none" />
                  <span className="relative flex h-2.5 w-2.5 mt-1.5 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                  </span>
                  <div className="relative">
                    <p className="text-[10px] text-emerald-300 uppercase tracking-widest font-bold mb-0.5">🤝 Contractually-binding pledge</p>
                    <p className="text-base sm:text-lg font-bold text-white leading-snug">
                      <span className="text-emerald-300">3× return</span> on your subscription in 90 days — or a <span className="text-emerald-300">full refund</span>.
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      No "we tried our best." A contract. <span className="text-emerald-400 group-hover:underline">Read the 90-day refund contract →</span>
                    </p>
                  </div>
                </div>
              </Link>

              {/* CTA Buttons — v35.30: primary CTA now drives to the 60-second
                  token-savings live demo (the wedge action). Demo login moved to
                  secondary. */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-2 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <MagneticButton strength={10} className="w-full sm:w-auto">
                  <Link to="/try" className="w-full sm:w-auto block" data-testid="hero-try-demo-btn">
                    <Button
                      size="lg"
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 text-base shadow-md shadow-emerald-500/20 transition-shadow hover:shadow-emerald-500/40 w-full sm:w-auto"
                    >
                      <Play className="w-4 h-4 mr-2 fill-current" />See your tokens drop 60% (60-sec demo)
                    </Button>
                  </Link>
                </MagneticButton>
                <MagneticButton strength={8} className="w-full sm:w-auto">
                  <Button
                    size="lg" variant="outline" onClick={handleDemoLogin} disabled={demoLoading}
                    className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 hover:border-emerald-400 hover:text-white px-6 h-12 text-base w-full sm:w-auto transition-colors"
                    data-testid="try-live-demo-btn"
                  >
                    {demoLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-emerald-300/30 border-t-emerald-300 rounded-full animate-spin" />Loading...
                      </span>
                    ) : (<><Calculator className="w-4 h-4 mr-2" />Explore the full dashboard</>)}
                  </Button>
                </MagneticButton>
              </div>

              {/* v34.63 — pricing whisper tightened from 4 phrases to 2.
                  "Start trial · From $199/mo" — gone are "no card required"
                  + "cancel anytime" which were already implied by "free trial". */}
              <p className="text-xs text-slate-400 mb-5 text-center lg:text-left animate-fade-in-up tabular-nums" style={{ animationDelay: '0.45s' }} data-testid="hero-pricing-whisper">
                Or <Link to="/pricing" className="text-emerald-300 hover:text-emerald-200 underline-offset-4 hover:underline font-semibold">start a free 14-day trial →</Link> · From <span className="text-emerald-300 font-semibold">$199/mo</span> · No card required
              </p>

              {/* v34 (Mar 2026): redundant "Free Snowflake Audit + Token Intelligence Suite"
                  secondary CTA row removed. Both are surfaced in the Free Tools carousel
                  + the Token Intel pill at the top of the hero. */}

              {/* v34 (Mar 2026): LaunchCounter "0 NEW PILOTS · 1 ROI REPORT · 205 PILOTS"
                  panel removed. User feedback: the "0 new pilots this week" was
                  weak social proof and added noise to the hero. */}

              {/* Secondary CTA strip — all trust/founder signals on one tight row */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-center lg:justify-start animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <button
                  onClick={() => setBookDemoOpen(true)}
                  className="group inline-flex items-center gap-2 text-sm text-emerald-300 hover:text-emerald-200 font-medium transition-colors"
                  data-testid="book-demo-btn"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Book a 15-min call with the founders</span>
                  <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </button>
                <div
                  className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30"
                  data-testid="founders-online-badge"
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400"></span>
                  </span>
                  <span className="text-[11px] font-semibold text-emerald-300 whitespace-nowrap">Reply within 4 hours</span>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-white/5 rounded-full border border-white/10">
                  <Monitor className="w-3 h-3 text-emerald-400" />
                  <span className="text-[11px] text-slate-400 whitespace-nowrap">No signup required</span>
                </div>
              </div>
            </div>

            {/* Right - Floating 3D Dashboard Preview (sticks while scrolling the long left stack) */}
            <div className="hidden lg:block animate-fade-in-up lg:sticky lg:top-24" style={{ animationDelay: '0.3s' }}>
              {/* v34.7 (May 2026) — restored cartieai.com production hero panel.
                  Per user feedback "I want it like cartieai.com but with updated data",
                  swapped the tail -f terminal back to the rich dashboard mockup
                  (3 KPI tiles + savings bar chart + token-leak event + live signal
                  feed + floating Anomaly/AI pills). All numbers updated to current
                  CARTIE state (26 engines, 12 features, etc). */}
              <div className="relative">
                {/* Outer emerald glow for depth */}
                <div className="absolute -inset-6 bg-emerald-500/10 rounded-3xl blur-3xl pointer-events-none" />
                <div className="relative">
                  {/* v34.68 (Jun 3 2026) — Nova anti-vanity fix: dashboard
                      animates from a "Connecting to AWS..." boot sequence
                      into the finished mockup. Plays once, ~3 seconds. */}
                  <HeroSkeletonReveal>
                    <HeroDashboardMockup />
                  </HeroSkeletonReveal>
                </div>
              </div>

              {/* v34 (Mar 2026): floating "AI Analysis Ready" + "Anomaly Detected"
                  accent cards removed — too many competing animated chips. */}
            </div>

            {/* v34.5 (Mar 2026) — hero inline stats (11.5/12 · 24 · 5 min) removed.
                The new LiveTerminalHero on the right already surfaces 3 live KPIs
                ($4,210 saved · 47.3% cache hit · 0.86s latency) which are more
                concrete + persuasive than the static ones. */}
          </div>
        </div>
      </section>

      {/* Trust Bar — security badges + v34.30 (Feb 2026) live visitor
          counter. Cloud marquee removed earlier (Feb 28).
          v34.37 (Feb 2026) — Every item is now a Link to its deep-dive
          page so visitors can drill into the proof behind each pill. */}
      {/* v34.x (Jun 2026) — Trust strip extracted to /components/landing/TrustStrip.js. */}
      <TrustStrip />

      {/* ===== v34.91 (Feb 13 2026) — "What CARTIE can do RIGHT NOW" 3-card row.
           Solves the discovery problem: with 189 pages, visitors need a
           tight 3-option entry into working free tools (no signup). ===== */}
      <WhatCartieCanDoNow />

      {/* v35.44 (Jul 4 2026) — Narrated 7-min full-application tour video. */}
      <TourVideo />

      {/* v34 (Mar 2026): Stats Bar section removed.
          User feedback: the "1st cohort · 90d · 24 · 132" grid felt like
          generic SaaS filler. The trust marquee above + the First-to-Market
          section below already carry the credibility load. */}

      {/* ============================================================
           v34 (Mar 2026) — First-to-Market PROMOTED to the fold.
           These 3 engines are the "why CARTIE" headline for HN skeptics.
           ============================================================ */}
      {/* v34.73 (Jun 9 2026) — First-to-market section removed from Landing
          to fight scroll fatigue. Full content lives at the dedicated
          subpage(s) linked from the 'Dive Deeper' grid below. */}


      {/* ===== Why CARTIE is different — the honest-FinOps moat ===== */}
      {/* v34.73 (Jun 9 2026) — Why Different / Moat section removed from Landing
          to fight scroll fatigue. Full content lives at the dedicated
          subpage(s) linked from the 'Dive Deeper' grid below. */}


      {/* ===== TOTAL COST OF AI — v34.54 interactive — Feb 2026 =====
           User feedback: "feels long, make it interactive."
           Collapsed from ~700px tall (heading + paragraph + bar + 3 detail rows
           + footnote) to ~360px (heading + slider + bar with $ amounts + inline
           CTAs + footnote). Visitor drags the slider, sees their OWN spend
           sliced 40/30/30, and each CTA personalizes to their dollar amount. */}
      {/* v34.80 (Jun 9 2026) — Inline "Total Cost of AI" calculator removed
           from landing. Three calculators on one page was overkill. The full
           interactive version lives at /tools/ai-cost-calculator (linked in
           the navbar) and the savings-focused RoiCalculator is still below.
           This slim strip teases the calculator without consuming a whole
           screen of real estate. */}
      {/* v34.x (Jun 2026) — TotalCostOfAiTeaser extracted to
           /components/landing/TotalCostOfAiTeaser.js. Visual + test IDs
           unchanged. */}
      <TotalCostOfAiTeaser />

      {/* ============================================================
           v34.29 (Feb 2026) — ScorecardTeaser MERGED into EnginesMarquee
           per user feedback — they were the same story (26 engines /
           27 capabilities) told in two places. Now both live in one
           section so visitors get the engine ribbon, then ONE summary
           card with the head-to-head receipt + a single CTA to the
           full scorecard. ScorecardTeaser.js still exists for other
           potential surfaces.
           ============================================================ */}

      {/* ===== 30-second product walkthrough — animated 4-act demo ===== */}
      {/* v34.5 (Mar 2026) — design-blueprint cut: 30-second walkthrough section removed for tighter scroll. */}


      {/* v34: First-to-Market section was PROMOTED to right after the Stats bar.
          Removed from this position to avoid duplication. */}

      {/* ============================================================
           v34 (Mar 2026) — Cloud + Data Integrations strip
           --------------------------------------------------------
           Per-cloud feature DEPTH. Visitors asked: "what exactly
           does CARTIE do for AWS / Azure / Snowflake?" — this
           answers in one screen. 6 LIVE today, 9 in progress.
           ============================================================ */}
      {/* v34.2 (Mar 2026): Cloud Integrations restyled as horizontal
          card carousel — colorful icon cards + arrow nav + dot indicators.
          See /app/frontend/src/components/CloudIntegrationsCarousel.js */}
      <CloudIntegrationsCarousel />


      {/* v34.2 (Mar 2026): EnginesMarquee — replaces the deleted 26 Engines grid
          with a single infinite-scrolling ribbon. Horizontal motion variety. */}
      <EnginesMarquee />

      {/* ============================================================
           v34 (Mar 2026) — Typography Manifesto
           --------------------------------------------------------
           A no-card, no-border, no-icon section. Just oversized type
           and a hard claim. Breaks the boxes-boxes pattern. Stripe /
           Linear use this style to slow visitors down before the next
           dense section. Sits between Cloud Integrations and the
           8-pillar Product Surface.
           ============================================================ */}
      {/* v34.73 (Jun 9 2026) — Manifesto section removed from Landing
          to fight scroll fatigue. Full content lives at the dedicated
          subpage(s) linked from the 'Dive Deeper' grid below. */}


      {/* ============================================================
           v34 (Mar 2026) — Complete Product Surface
           --------------------------------------------------------
           Capability map. Visitors get one screen that says
           "yes, we do that too" across 8 product pillars. Every
           pillar links to its deepest authed surface or public
           free-tool. No marketing words — just the surface area.
           ============================================================ */}
      <section id="product-surface" className="py-16 px-4 relative overflow-hidden border-t border-white/5 scroll-mt-24" data-testid="landing-product-surface">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent" />
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
              <Gauge className="w-3 h-3" /> Complete coverage · 8 pillars · 70+ surfaces
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.015em] leading-[1.15] text-white max-w-4xl mx-auto">
              The whole FinOps stack <span className="text-emerald-400 whitespace-nowrap">in one tool</span>.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
              Replace 9 vendors (Vantage + CloudHealth + CloudZero + Kubecost + Helicone + Datadog + Watershed + Spot.io + IBM Turbonomic). Every pillar below is a real, in-product surface — not a roadmap promise.
            </p>
          </div>

          {/* v34.51 (Feb 2026): flip-card grid replaces the "newspaper" 2-col
              prose list. Front of each card = icon + pillar + 1-line value prop
              + surface count. Hover/tap flips to reveal the full feature list
              + an "Explore →" button that navigates. Cuts vertical height
              ~50% on desktop, scannable in 5 sec, deep-dive on demand.
              Uses pure CSS 3D transform (perspective + rotateY) so no JS lib.
          */}
          <style>{`
            .flip-card { perspective: 1000px; }
            .flip-card-inner {
              position: relative;
              width: 100%;
              height: 100%;
              transition: transform 0.55s cubic-bezier(0.4, 0.0, 0.2, 1);
              transform-style: preserve-3d;
            }
            .flip-card:hover .flip-card-inner,
            .flip-card:focus-within .flip-card-inner,
            .flip-card.flipped .flip-card-inner {
              transform: rotateY(180deg);
            }
            .flip-card-front, .flip-card-back {
              position: absolute;
              inset: 0;
              backface-visibility: hidden;
              -webkit-backface-visibility: hidden;
            }
            .flip-card-back { transform: rotateY(180deg); }
          `}</style>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              // v34.52 (Feb 2026): per-pillar accents rebalanced so no two same-color
              // cards sit horizontally adjacent OR vertically stacked in the 4-col
              // grid. 8 distinct accents: emerald · rose · sky · amber · teal · violet · fuchsia · pink.
              { Icon: Brain, pillar: 'AI / LLM Suite', tagline: 'Token leaks fixed, prompts compacted, every model routed', accent: 'emerald', items: ['Token Intelligence Suite', 'Semantic Cache (real embeddings)', 'Prompt Compactor', 'LLM Proxy + Smart Router', 'Prompt Playground', 'Eval Runner + QA Failover', 'Fine-Tuning ROI', '"Why?" explainer'], cta: '/token-intelligence-suite' },
              { Icon: AlertOctagon, pillar: 'Anomaly & Causality', tagline: 'Spot the spike, trace the cause, show the receipt', accent: 'rose', items: ['Anomaly Detection v2 + Auto-resolve', 'Spike Autopsy', 'Invoice Autopsy', 'Causal FinOps Bot', 'Cost Bisect (git-bisect for $)', 'Token Leak Detector', 'Bill Shock Prevention', 'Spend Freeze (emergency stop)'], cta: '/anomalies' },
              { Icon: Cpu, pillar: 'Cloud Cost', tagline: 'Every cloud, every commitment, every zombie pod', accent: 'amber', items: ['AWS · Azure · GCP', 'Snowflake · Databricks', 'DigitalOcean · K8s', 'RI/SP Commitment Coach', 'Zombie Killer', 'Storage Detective', 'Resource Scheduler', 'One-Click Fix + Auto-PR'], cta: '/cloud-integrations' },
              { Icon: Leaf, pillar: 'Carbon & ESG', tagline: 'Ship cheaper code that also costs the planet less', accent: 'teal', items: ['Carbon Cost Pareto', 'Carbon Optimizer (active scheduling)', 'Scope 3 emissions', 'Region × CO₂ optimizer', 'ESG-ready reports'], cta: '/carbon-cost' },
              { Icon: Tags, pillar: 'Tags & Compliance', tagline: 'SOC 2-clean tags, evidence on tap', accent: 'fuchsia', items: ['Smart Tag Fixer', 'Tag Canon · Tag Drift', 'Compliance Mapper (SOC 2 / ISO)', 'Algorithm Correctness manifest', 'SOC 2 Readiness', 'Audit Export'], cta: '/tagging' },
              { Icon: Presentation, pillar: 'CFO Tools', tagline: 'Board decks, vendor intel, chargebacks — without Apptio', accent: 'violet', items: ['AI CFO Advisor + Command Center', 'Board Deck Generator', 'Vendor Deal Intel', 'Customer P&L · Unit Econ', 'Cost Scorecard (gamified)', 'Cost Allocation · Cost Centers', 'Virtual Tenant chargebacks', 'Industry Intel · Multi-currency'], cta: '/ai-cfo' },
              { Icon: BarChart3, pillar: 'Forecasting & Simulation', tagline: "See tomorrow's bill before today's deploy", accent: 'sky', items: ['Cost Forecasting + Forward Predict', 'Decision Simulator', 'What-If Calculator', 'Cost Time Machine', 'PR Cost Predictor (Terraform)', 'Egress Predictor', 'Egress Shield (live flow map)'], cta: '/cost-forecasting' },
              { Icon: Bell, pillar: 'Notifications & Surfaces', tagline: 'Slack, IDE, terminal, MCP — meets engineers where they live', accent: 'pink', items: ['Slack @cartie bot', 'Monday Brief · Weekly Digest', 'Voice Assistant', 'VS Code extension', 'npm CLI · MCP server', 'GitHub Auto-PR', 'Custom Dashboard Builder', 'Setup in 15 minutes'], cta: '/notifications' },
            ].map((p, i) => {
              const Ic = p.Icon;
              const num = String(i + 1).padStart(2, '0');
              // v34.52 — neon-lit icon tiles (solid saturated bg + white icon + outer glow)
              // matching the EnginesMarquee + FreeToolsCarousel aesthetic.
              const accentMap = {
                emerald: { ring: 'border-emerald-500/30', text: 'text-emerald-300', glow: 'shadow-[0_0_28px_-10px_rgba(16,185,129,0.7)]', iconTile: 'bg-emerald-500 shadow-[0_0_18px_0_rgba(16,185,129,0.55)]', dot: 'bg-emerald-400' },
                rose:    { ring: 'border-rose-500/30',    text: 'text-rose-300',    glow: 'shadow-[0_0_28px_-10px_rgba(244,63,94,0.7)]',  iconTile: 'bg-rose-500 shadow-[0_0_18px_0_rgba(244,63,94,0.55)]',     dot: 'bg-rose-400' },
                sky:     { ring: 'border-sky-500/30',     text: 'text-sky-300',     glow: 'shadow-[0_0_28px_-10px_rgba(14,165,233,0.7)]', iconTile: 'bg-sky-500 shadow-[0_0_18px_0_rgba(14,165,233,0.55)]',    dot: 'bg-sky-400' },
                amber:   { ring: 'border-amber-500/30',   text: 'text-amber-300',   glow: 'shadow-[0_0_28px_-10px_rgba(245,158,11,0.7)]', iconTile: 'bg-amber-500 shadow-[0_0_18px_0_rgba(245,158,11,0.55)]',   dot: 'bg-amber-400' },
                teal:    { ring: 'border-teal-500/30',    text: 'text-teal-300',    glow: 'shadow-[0_0_28px_-10px_rgba(20,184,166,0.7)]', iconTile: 'bg-teal-500 shadow-[0_0_18px_0_rgba(20,184,166,0.55)]',   dot: 'bg-teal-400' },
                violet:  { ring: 'border-violet-500/30',  text: 'text-violet-300',  glow: 'shadow-[0_0_28px_-10px_rgba(139,92,246,0.7)]', iconTile: 'bg-violet-500 shadow-[0_0_18px_0_rgba(139,92,246,0.55)]', dot: 'bg-violet-400' },
                fuchsia: { ring: 'border-fuchsia-500/30', text: 'text-fuchsia-300', glow: 'shadow-[0_0_28px_-10px_rgba(217,70,239,0.7)]', iconTile: 'bg-fuchsia-500 shadow-[0_0_18px_0_rgba(217,70,239,0.55)]', dot: 'bg-fuchsia-400' },
                pink:    { ring: 'border-pink-500/30',    text: 'text-pink-300',    glow: 'shadow-[0_0_28px_-10px_rgba(236,72,153,0.7)]', iconTile: 'bg-pink-500 shadow-[0_0_18px_0_rgba(236,72,153,0.55)]',   dot: 'bg-pink-400' },
              };
              const ac = accentMap[p.accent] || accentMap.emerald;
              return (
                <ScrollReveal key={i} direction="up" delay={(i % 4) * 60}>
                  <div className="flip-card h-[260px]" data-testid={`pillar-card-${i}`}>
                    <div className="flip-card-inner">
                      {/* FRONT */}
                      <div className={`flip-card-front rounded-2xl border ${ac.ring} bg-slate-900 ${ac.glow} p-5 flex flex-col justify-between`}>
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <div className={`w-11 h-11 rounded-xl ${ac.iconTile} ring-1 ring-white/20 flex items-center justify-center`}>
                              <Ic className="w-5 h-5 text-white" strokeWidth={2.4} />
                            </div>
                            <span className="font-mono text-[10px] text-slate-500 tabular-nums">{num}</span>
                          </div>
                          <h3 className="font-display text-base font-bold text-white tracking-tight mb-2 leading-tight">{p.pillar}</h3>
                          <p className="text-[13px] text-slate-400 leading-relaxed">{p.tagline}</p>
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                          <span className={`text-[11px] font-mono uppercase tracking-widest ${ac.text}`}>
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${ac.dot} mr-1.5 align-middle`} />
                            {p.items.length} surfaces
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">Hover →</span>
                        </div>
                      </div>
                      {/* BACK */}
                      <div className={`flip-card-back rounded-2xl border ${ac.ring} bg-slate-950 p-5 flex flex-col`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-7 h-7 rounded-lg ${ac.iconTile} ring-1 ring-white/20 flex items-center justify-center shrink-0`}>
                            <Ic className="w-3.5 h-3.5 text-white" strokeWidth={2.4} />
                          </div>
                          <h4 className={`text-[11px] font-mono uppercase tracking-widest ${ac.text}`}>{p.pillar}</h4>
                        </div>
                        <ul className="flex-1 overflow-y-auto space-y-1.5 text-[12px] text-slate-300 leading-snug pr-1 min-h-0">
                          {p.items.map((it) => (
                            <li key={it} className="flex items-start gap-2">
                              <span className={`mt-1 w-1 h-1 rounded-full ${ac.dot} shrink-0`} />
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                        <Link
                          to={p.cta}
                          className={`mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border ${ac.ring} ${ac.text} text-[12px] font-semibold transition-colors`}
                          data-testid={`pillar-cta-${i}`}
                        >
                          Explore <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">
            Built to <strong className="text-white">replace 9 single-purpose vendors</strong>. One subscription, one login, one unified P&L. Migration wizards for Vantage · CloudHealth · CloudZero · Kubecost · Spot.io included.
          </p>
        </div>
      </section>

      {/* ===== Free Tools strip — turn the 4 best lead magnets into the first thing CFOs see ===== */}
      {/* v34.73 (Jun 9 2026) — Free Tools strip section removed from Landing
          to fight scroll fatigue. Full content lives at the dedicated
          subpage(s) linked from the 'Dive Deeper' grid below. */}


      {/* v32.4 (Feb 25 2026): "LLM Engineering Suite" 140-line landing strip removed.
          The 5 surfaces are linked from the Moat row above ("AI Cost / Token Intel")
          and have a full deep-dive at /llm-eng-suite. Landing kept tighter; the
          dev-tool wedge is repeated less times. */}

      {/* ============================================================
           v33.4 (Feb 27 2026) — Developer-Loved strip
           --------------------------------------------------------
           Three surfaces engineers literally use every day:
           npx CLI · MCP server · one-click PR to their IaC repo.
           This is the "FinOps tool engineers don't hate" payoff.
           ============================================================ */}
      {/* v34.73 (Jun 9 2026) — Developer-loved section removed from Landing
          to fight scroll fatigue. Full content lives at the dedicated
          subpage(s) linked from the 'Dive Deeper' grid below. */}


      {/* ============================================================
           v34.6 (Mar 2026) — Developer Quickstart · code drop-in
           --------------------------------------------------------
           Pure tech, zero marketing. 5-line install, tabbed editor,
           emerald syntax highlighting, easter-egg input.
           Source: DeveloperQuickstart.js
           ============================================================ */}
      <DeveloperQuickstart />

      {/* v32.5 (Feb 27 2026): standalone "AI Cost Calculator" full-bleed section removed —
          duplicated (a) the "Cloud LLM tokens" slice in the Total Cost of AI section
          and (b) the Savings Calculator card in the Free Tools strip. The calculator
          deep-dive still lives at /tools/ai-cost-calculator; both surfaces above
          link to it. Page now ~85 lines lighter and reads in one downward sweep. */}

      {/* v34 (Mar 2026): "26 Smart Engines" grid removed.
          The Complete Product Surface section above already lists every engine
          across 8 pillars. Duplicating the engines as their own dedicated
          section added ~75 lines of visual noise without new info. The
          dedicated /patent-portfolio page remains canonical for the deep-dive.
      */}

      {/* ===== Sub-system Z · Budget Negotiator — advisor-feedback flagship ===== */}
      {/* v34.5 (Mar 2026) — design-blueprint cut: Budget Negotiator section removed for tighter scroll. */}


      {/* ===== PR-Time Cost Prediction — public lead-magnet demo strip ===== */}
      {/* v34.5 (Mar 2026) — design-blueprint cut: PR Cost Preview section removed for tighter scroll. */}


      {/* v26: removed "Trifecta" (editor/PR/audit strip) — content already covered by
          the LLM Engineering Suite and Budget Negotiator sections above. Also removed
          the AWS Cost Anomaly Detection comparison — too niche for the launch hero;
          the FAQ keeps the response, and /honest-scorecard has the full matrix. */}


      {/* v32.4 (Feb 25 2026): "Migration Wizard" landing strip removed — moved to
          /integrations?tab=migrate. Top-of-funnel landing kept tighter; users who
          already use Vantage / CloudHealth / CloudZero / Kubecost / Datadog / Spot.io / IBM Turbonomic will see the
          migration CTA on the Integrations page after sign-in. */}

      {/* v32.4 (Feb 25 2026): "Researched · Reasoned · Rebuilt" competitor strip
          deleted from Landing — the same competitor positioning is now richer on
          /honest-scorecard (linked from the hero) and /patent-portfolio's head-to-
          head table. Keeps Landing focused on the moat + Total Cost of AI story
          without duplicating the comparison three times. */}

      {/* v34 (Mar 2026): "How It Works" 3-step section removed.
          The walkthrough video + hero subtitle already explain the flow.
          Adding a separate "Connect → Analyze → Save" 3-step grid was filler. */}

      {/* v34 (Mar 2026): "Data Security" big section compressed to 1-line trust strip.
          Full security details still live at /security. */}
      {/* v34.73 (Jun 9 2026) — Security strip section removed from Landing
          to fight scroll fatigue. Full content lives at the dedicated
          subpage(s) linked from the 'Dive Deeper' grid below. */}


      {/* v26: removed "Plus the FinOps Basics" marquee — it was a 10-card scrolling strip
          with white-icon backgrounds that looked broken on the dark theme + duplicated
          the engine teasers above. ROI Calculator section follows directly. */}

      {/* v32.4 (Feb 25 2026): inline ROI Calculator section deleted — it duplicated
          the hero's 3× ROI Pledge card. The dedicated /pricing#pledge page is the
          canonical surface for the binding-refund contract; the live savings
          marquee is shown via the LiveSavingsTicker on authed pages. */}

      {/* v34.56 (Feb 2026) — Final-CTA pledge section REMOVED (was ~600px tall).
          The hero already carries the contractually-binding pledge card; a second
          full-section pledge near the bottom read as repetition / try-too-hard.
          The 4 differentiation metrics (26/27 vs Helicone · $0 cache · 15-40%
          token cut · 70% bill reduction) were the only unique content in the
          deleted section — preserved here as a compact horizontal proof strip. */}
      {/* v34.77 (Jun 9 2026) — Proof strip ("By the numbers") removed.
           Same stats already appear higher up in ReliabilityStrip + hero
           dashboard. Full math lives at /honest-scorecard (linked from
           the "The moat" Dive Deeper card). */}

      {/* Free Snowflake Health Score CTA — SEO + lead-gen wedge */}
      {/* Built in the open section removed (Iter84f) — BuildMomentumTeaser
           was linking into /build/momentum which now returns 404 when the
           Build-in-Public toggle is OFF. We can bring it back by flipping
           the toggle in /admin/pre-flight at any time. */}
      {/* <section id="built-in-the-open"><BuildMomentumTeaser /></section> */}

      {/* v34.89 (Feb 2026) — TopReadsWidget RELOCATED to right above the FAQ
           section. The "Top reads → now answering your questions" flow
           reads more naturally than dropping blog content in the middle of
           the funnel. See <TopReadsWidget /> placement just before #landing-faq. */}

      {/* v34.68–v34.71 (Jun 3 2026) — Nova review batch + balance fix:
          User: "we help both engineers AND FinOps." This funnel intentionally
          alternates CFO/engineer surfaces so both audiences land moments of
          drama and stay on the page.

          0. ReliabilityStrip          — trust counters (mixed)
          1. ConsolidationMath         — CFO 🟢  "$5K stack → $1,997 CARTIE"
          2. RoiCalculator             — CFO 🟢  interactive savings + email capture
          3. AiCfoBriefing             — CFO 🟢  Monday-morning briefing (v34.71 balance)
          4. SpotInferenceArbitrageur  — ENG 🔵  Real-Time Token Arbitrage (Nova R4 #1)
          5. OneClickRefund            — ENG/CFO  SLA refund (Nova R4 #4)
          6. CausalAttribution         — ENG 🔵  spike → line of code
          7. CartieCliMockup           — ENG 🔵  terminal magic moment
          8. EncryptionManifest        — CFO/CISO 🟢  hard-infrastructure trust
          9. AntiLockInGuarantee       — CFO/ENG 🟢🔵  leave in one line
         10. WhoNotFor                 — mixed exclusion list
         11. PilotWall                 — ENG 🔵  founding-10 invitation

          Result: 3 CFO sections back-to-back near top → 4 engineer sections
          in the middle → 2 trust/defuse sections → exclusion → invitation. */}
      {/* v34.80 — <ReliabilityStrip /> removed from landing.
           Its two strongest stats (99.97% uptime, 0 Auto-PR outages) now
           live as inline trust pills above. Full component preserved for
           /security page use. */}
      {/* v34.74 (Jun 9 2026) — ConsolidationMath, AiCfoBriefing,
           SpotInferenceArbitrageur, OneClickRefund, EncryptionManifest
           removed for scroll fatigue. All accessible via the Dive Deeper
           card grid below. Components still live in /components for their
           dedicated subpages. */}
      <RoiCalculator />
      {/* v35.36 (Jun 2026) — PricingTransparencyWidget removed from landing:
          three calculator-style surfaces (TotalCostOfAiTeaser, RoiCalculator,
          PricingTransparency) was one too many. Full version lives on /pricing. */}

      <CausalAttribution />
      {/* v34.75 (Jun 9 2026) — CartieCliMockup removed. Full CLI demo
           lives at /developers (linked from the 'For devs' Dive Deeper
           card). Component preserved in /components for that subpage. */}

      {/* v34.86 (Feb 2026) — Inline CLI install strip.
           Closes the discovery gap for @cartieai/cli on npm. */}
      <CliInstallStrip />

      {/* v34.80 — <AntiLockInGuarantee /> condensed to inline pill strip.
           Full 3-card grid + contract clauses live at /security. */}
      <AntiLockInStrip />
      {/* v34.76 (Jun 9 2026) — WhoNotFor + PilotWall removed from landing.
           WhoNotFor lives at /honest-scorecard. PilotWall lives at /pilot. */}

      {/* v34.66 (May 30 2026) — Wall of Love slotted before FAQ.
          The component is self-contained: shows real testimonials once we have
          them, otherwise renders the EmptyWallWithInlineForm pre-launch state
          (founder-honest "be the first to share" + inline submit form that
          POSTs to /api/testimonials/submit). Zero-customer-friendly social-
          proof flywheel that improves itself the moment one testimonial lands. */}
      <WallOfLove />

      {/* FAQ — top-of-funnel objections handled before the newsletter band */}
      {/* v34.x (Jun 2026) — Dive Deeper section extracted to
           /components/landing/DiveDeeperSection.js to slim Landing.js.
           Visual + test IDs unchanged. Arbitrage engine marquee link now points
           to /engine-27 (was /token-intelligence-suite). */}
      <DiveDeeperSection />

      {/* v34.89 — TopReadsWidget (relocated from earlier scroll position).
           Sits right before the FAQ for a clean "read more → now you have
           questions?" flow. */}
      <TopReadsWidget />

      {/* v35.36 (Jun 2026) — TopAskedFaqsWidget removed from landing: it sat
           directly above the full FAQ section and told the same story twice.
           Landing-length trim per founder feedback. */}

      <section className="relative py-12 px-4" id="landing-faq">
        <div className="max-w-4xl mx-auto">
          <FAQSection
            title="Questions, answered honestly."
            subtitle="Pricing, security, integration effort, refunds — the eight questions every CFO and engineer asks before signing up. No marketing fluff."
            items={faqsByPage.landing}
            testIdPrefix="landing-faq"
          />
        </div>
      </section>

      {/* The FinOps Brief newsletter */}
      {/* Newsletter + Footer band — neural-network animation extends from hero to bottom */}
      <div className="relative">
        <FooterBackground />

      {/* v34 (Mar 2026): standalone Newsletter section removed.
          Newsletter signup moved into the footer to reduce mid-page interruptions.
          Visitors who want to subscribe can do so at the bottom of the page. */}

        <footer className="relative py-16 px-4 border-t border-white/5 bg-slate-950/40 backdrop-blur-[2px]">
        <div className="max-w-6xl mx-auto">
          {/* v34.65 — centered newsletter strip + "Share your CARTIE story →" CTA.
              Was left-aligned (felt floating). Now centered as a deliberate end-of-
              page moment, paired with the testimonial-collection CTA that powers
              /api/testimonials/submit (already used on /about for the WallOfLove
              and on /share-your-voice). Two-line layout so both feel co-equal. */}
          <div className="mb-12 pb-10 border-b border-white/5 text-center" data-testid="footer-newsletter">
            <div className="max-w-md mx-auto mb-3">
              <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-1">Stay close</p>
              <p className="text-xs text-slate-400 leading-relaxed mb-4">
                One weekly note from the founder. Cost wins, new engines, the things competitors won't tell you.
              </p>
            </div>
            <div className="flex justify-center">
              <NewsletterSignup source="landing_footer" compact />
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-slate-500">
              <span>Already shipping with CARTIE?</span>
              <Link to="/share-your-voice" className="text-emerald-300 hover:text-emerald-200 font-semibold underline-offset-4 hover:underline inline-flex items-center gap-1" data-testid="footer-share-story">
                Share your story <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* v34.57 (Feb 2026) — Footer slimmed:
              Product: 9 items → 5 + "All features →"
              Free Tools: 15 items → 5 + "Browse all →"
              Company + Legal kept tight at 5 each.
              Visitor sees the high-converting links; explorers click through.
          */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand col */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-8 h-8 object-contain" />
                <span className="text-lg font-bold"><span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup></span>
              </div>
              {/* v34.99 (Feb 14 2026) — Official tagline as a small monospace
                  pill under the brand wordmark in the footer. */}
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-300/80 mb-3" data-testid="footer-tagline">
                The Financial OS for Cloud &amp; AI Spend
              </p>
              <p className="text-xs text-slate-400 leading-relaxed">
                The AI-native FinOps platform that finds &amp; fixes cloud waste across AWS, Azure, GCP, Snowflake, Databricks &amp; DigitalOcean.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <a href="https://twitter.com/cartieai" target="_blank" rel="noreferrer" className="text-slate-500 hover:text-emerald-300 transition-colors" aria-label="Twitter" data-testid="footer-social-twitter">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://github.com/cartieai" target="_blank" rel="noreferrer" className="text-slate-500 hover:text-emerald-300 transition-colors" aria-label="GitHub" data-testid="footer-social-github">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.56v-2.13c-3.2.69-3.87-1.36-3.87-1.36-.52-1.34-1.28-1.69-1.28-1.69-1.05-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.71 1.26 3.37.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.28 1.19-3.09-.12-.29-.51-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11.06 11.06 0 015.79 0c2.21-1.49 3.18-1.18 3.18-1.18.62 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.42-2.69 5.4-5.25 5.68.41.36.77 1.06.77 2.13v3.16c0 .31.21.66.79.55C20.21 21.39 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5z"/></svg>
                </a>
                <a href="https://linkedin.com/company/cartieai" target="_blank" rel="noreferrer" className="text-slate-500 hover:text-emerald-300 transition-colors" aria-label="LinkedIn" data-testid="footer-social-linkedin">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.45 20.45h-3.55v-5.56c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.13 1.45-2.13 2.94v5.65H9.36V9h3.41v1.56h.05a3.74 3.74 0 013.37-1.85c3.6 0 4.27 2.37 4.27 5.46zM5.34 7.43a2.06 2.06 0 11-.01-4.12 2.06 2.06 0 010 4.12zM7.12 20.45H3.56V9h3.56zM22.22 0H1.77C.8 0 0 .78 0 1.74v20.52C0 23.22.8 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.74V1.74C24 .78 23.2 0 22.22 0z"/></svg>
                </a>
              </div>
            </div>

            {/* Product col — 5 highest-signal items */}
            <div>
              <p className="text-xs font-bold tracking-widest text-slate-300 uppercase mb-3">Product</p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/bill-detective" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="footer-bill-detective-link">Bill Detective <span className="text-[10px] font-bold text-emerald-300">NEW</span></Link></li>
                <li><Link to="/agent-economics" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="footer-agent-economics-link">Agent Economics <span className="text-[10px] font-bold text-fuchsia-300">WORLD-FIRST</span></Link></li>
                <li><Link to="/embedding-audit" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="footer-embedding-audit-link">Embedding Audit <span className="text-[10px] font-bold text-fuchsia-300">WORLD-FIRST</span></Link></li>
                <li><Link to="/pipeline-dedup" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="footer-pipeline-dedup-link">Pipeline Dedup <span className="text-[10px] font-bold text-fuchsia-300">WORLD-FIRST</span></Link></li>
                <li><Link to="/wall-of-fame" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="footer-wall-of-fame-link">Wall of Fame <span className="text-[10px] font-bold text-emerald-300">NEW</span></Link></li>
                <li><Link to="/token-intelligence-suite" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5">Token Intelligence <span className="text-[10px] font-bold text-emerald-300">NEW</span></Link></li>
                <li><Link to="/cli" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="footer-cli-link">CLI &amp; MCP <span className="text-[10px] font-mono text-emerald-400">npm</span></Link></li>
                <li><Link to="/integrations" className="hover:text-white transition-colors">Integrations</Link></li>
                <li><Link to="/vs" className="hover:text-white transition-colors">Compare vs Datadog · Helicone</Link></li>
                <li><Link to="/trust" className="hover:text-emerald-300 transition-colors">Trust</Link></li>
                <li><Link to="/forecasts-graded" className="hover:text-emerald-300 transition-colors">Forecasts, graded</Link></li>
                <li className="pt-1.5 mt-1.5 border-t border-white/5"><Link to="/patent-portfolio" className="text-emerald-400 hover:text-emerald-300 transition-colors text-xs font-semibold">All 26 engines →</Link></li>
              </ul>
            </div>

            {/* Free tools col — 5 highest-converting only */}
            <div>
              <p className="text-xs font-bold tracking-widest text-slate-300 uppercase mb-3">Free Tools</p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/tools/ai-cost-calculator" className="hover:text-emerald-300 transition-colors font-semibold text-emerald-400">AI Cost Calculator</Link></li>
                <li><Link to="/tokens" className="hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="footer-token-pricing-link">LLM Pricing Index <span className="text-[10px] font-bold text-emerald-300">NEW</span></Link></li>
                <li><Link to="/bill-score" className="hover:text-emerald-300 transition-colors">Bill Score Quiz</Link></li>
                <li><Link to="/savings-calculator" className="hover:text-emerald-300 transition-colors">Savings Calculator</Link></li>
                <li><Link to="/decision-simulator" className="hover:text-emerald-300 transition-colors">Decision Simulator</Link></li>
                <li><Link to="/free-snowflake-audit" className="hover:text-emerald-300 transition-colors">Snowflake Audit</Link></li>
                <li className="pt-1.5 mt-1.5 border-t border-white/5"><Link to="/free-tools" className="text-emerald-400 hover:text-emerald-300 transition-colors text-xs font-semibold">Browse all 7 free tools →</Link></li>
              </ul>
            </div>

            {/* Company col */}
            <div>
              <p className="text-xs font-bold tracking-widest text-slate-300 uppercase mb-3">Company</p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/about" className="hover:text-white transition-colors">Our Story</Link></li>
                <li><Link to="/brand" className="hover:text-white transition-colors" data-testid="footer-brand-link">Brand kit</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/developers" className="hover:text-white transition-colors">Developer Docs</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                {/* v34.93 — Public status page link with live dot. Visible trust signal. */}
                <li>
                  <Link
                    to="/status"
                    className="inline-flex items-center gap-1.5 hover:text-emerald-300 transition-colors"
                    data-testid="footer-status-link"
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                    </span>
                    Status
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal col */}
            <div>
              <p className="text-xs font-bold tracking-widest text-slate-300 uppercase mb-3">Legal</p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
                <li><Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link></li>
                <li><Link to="/subprocessors" className="hover:text-white transition-colors">Subprocessors</Link></li>
                <li><Link to="/legal/templates" className="hover:text-emerald-300 transition-colors" data-testid="footer-msa-dpa-link">MSA + DPA templates</Link></li>
                <li><a href="mailto:hello@cartieai.com" className="hover:text-emerald-300 transition-colors">hello@cartieai.com</a></li>
                <li><a href="tel:+15126217610" className="hover:text-emerald-300 transition-colors" data-testid="footer-phone-link">+1 (512) 621-7610</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom row */}
          <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-400" data-testid="footer-legal-entity">
              &copy; 2026 <span className="text-slate-300">CARTIEAI LLC</span> · Registered in Texas, USA · All rights reserved.
              <br className="hidden sm:inline" />
              <span className="text-slate-500">Founded by Lakshmi Kiranmai Guduru · </span>
              <a href="mailto:hello@cartieai.com" className="text-emerald-400 hover:text-emerald-300">hello@cartieai.com</a>
            </p>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <Link
                to="/system-status"
                className="flex items-center gap-1.5 hover:text-emerald-300 transition-colors"
                data-testid="footer-status-link"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                All systems operational
              </Link>
            </div>
          </div>
        </div>
      </footer>
      </div>

      {/* BuiltInPublicChip removed (Iter84f) — floating chip was part of the
           Build-in-Public surface that's now toggled off. */}
      {/* <BuiltInPublicChip /> */}

      {/* CSS Animations */}
      <style>{`
        @keyframes section-target-pulse {
          0%   { box-shadow: inset 0 0 0 0 rgba(16, 185, 129, 0); }
          25%  { box-shadow: inset 0 0 0 9999px rgba(16, 185, 129, 0.06); }
          100% { box-shadow: inset 0 0 0 9999px rgba(16, 185, 129, 0); }
        }
        .section-target-pulse {
          animation: section-target-pulse 1.6s ease-out;
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out both;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes chart-grow {
          from { transform: scaleY(0); }
          to { transform: scaleY(1); }
        }
        .animate-chart-grow {
          animation: chart-grow 0.8s ease-out both;
          transform-origin: bottom;
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.5s ease-out both;
        }
        .perspective-\\[1200px\\] { perspective: 1200px; }
        .hover\\:rotate-y-2:hover { transform: rotateY(2deg); }
        .hover\\:rotate-x-1:hover { transform: rotateX(1deg); }
        @keyframes marquee-left-local {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right-local {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .animate-marquee-left {
          animation: marquee-left-local 30s linear infinite;
        }
        .animate-marquee-right {
          animation: marquee-right-local 30s linear infinite;
        }
        .animate-marquee-left:hover, .animate-marquee-right:hover {
          animation-play-state: paused;
        }
      `}</style>

      <BookDemoModal open={bookDemoOpen} onOpenChange={setBookDemoOpen} />
    </div>
  );
};

export default Landing;
